import { call, delay, fork, put, select, take, takeLatest } from 'redux-saga/effects';
import { replace } from 'redux-first-history';
import { stringify } from 'query-string';
import { ContactField } from '@dev/ee-api/ee-api-models';
import { includes, isEmpty } from 'lodash-es';
import { isAxiosError } from 'axios';
import React from 'react';
import i18n from '@/../i18n';
import { HideToast, ShowToast } from '@/store/toast/actions';
import {
    DeleteContactFieldError,
    DeleteContactFieldSuccess,
    GetContactFieldsError,
    GetContactFieldsSuccess,
    PutContactFieldError,
    PutContactFieldSuccess,
} from '@/store/customFields/actions';
import { customFieldsNameBackendErrorMessageID, GetContactFieldsStartAction } from '@/store/customFields/types';
import { SetFields } from '@/store/customFields/actions';
import { createContactField, deleteContactField, getContactFields } from '@/services/ElasticApi';
import { ShowDialog } from '@/store/dialog/actions';
import { ApplicationState } from '@/store';
import { DEFAULT_PER_PAGE_COUNT } from '@/services/constants';
import { MergeFieldsLoadStart, getMergeFieldsStore } from '@/store/mergeFields/actions';
import { MergeFieldsActionTypes } from '@/store/mergeFields/types';
import { DeletionNotAllowedDialog } from '@/services/Dialog/DeletionNotAllowedDialog';
import { matchErrorMessagesForUsageInFeatures } from '@/services/tools/matchErrorMessages';
import { Logger } from '@/services/errors/appLogger';

const getLoadedCustomFields = (state: ApplicationState): ContactField[] => state.customFields.collection;

function* getcontactFields(action: GetContactFieldsStartAction): any {
    try {
        const { limit = DEFAULT_PER_PAGE_COUNT, offset = 0, search, isUrlUpdateDisabled } = action.payload;

        const activePage = (offset + limit) / limit;

        const urlQueryParams = {
            page: activePage,
            perPage: limit,
            ...(search ? { search } : {}),
        };

        if (!isUrlUpdateDisabled) {
            yield put(replace({ search: stringify(urlQueryParams) }));
        }
        let contactFields = yield select(getLoadedCustomFields);

        if (isEmpty(contactFields)) {
            contactFields = yield call(getContactFields);
        }

        yield put(GetContactFieldsSuccess(contactFields));
    } catch (e) {
        yield put(GetContactFieldsError());
        Logger.error(e);
    }
}

function* watchGetContactFields(): any {
    yield takeLatest('@@contactfields/FETCH_START', getcontactFields);
}

function* putcontactField(action): any {
    try {
        const contactField = yield call(createContactField, action.payload.contactField);
        const field = { Name: contactField.Name, Length: contactField.Length, Type: contactField.FieldType };
        const contactfields = yield select(getLoadedCustomFields);
        const newContactFields = [...contactfields];
        newContactFields.unshift(field); //for showing field on top of other fields after creation

        yield put(PutContactFieldSuccess(contactField.Name, newContactFields, ''));
        yield put(MergeFieldsLoadStart());
        yield take(MergeFieldsActionTypes.LOAD_SUCCESS);

        const { data } = yield select(getMergeFieldsStore);
        if (action.payload.successCallback) {
            const newMergeField = data?.MergeFields?.find((item) => item.Name === field.Name);
            yield call(action.payload.successCallback, newMergeField);
        }
        yield put(
            ShowToast({
                severity: 'info',
                message: i18n.t('customfieldadded'),
                duration: 6000,
                onClose: HideToast,
            })
        );
    } catch (e) {
        const fieldType = action.payload.currentFieldType;
        const errorMessage = isAxiosError(e) ? e?.response?.data?.Error : e?.message;
        const errorMessageId = isAxiosError(e) && e?.response?.data?.MessageID;

        if (includes(customFieldsNameBackendErrorMessageID, errorMessageId)) {
            return yield put(PutContactFieldError(fieldType, i18n.t('duplicatefieldname')));
        }

        yield put(
            ShowDialog({
                title: i18n.t('cannotaddcustomfield'),
                body: errorMessage,
                cancelLabel: i18n.t('close'),
            })
        );

        yield put(PutContactFieldError(fieldType));
    }
}

function* watchPutContactField(): any {
    yield takeLatest('@@contactfields/CREATE_START', putcontactField);
}

function* delContactField(action): any {
    try {
        const contactFields = yield select(getLoadedCustomFields);
        yield call(deleteContactField, action.payload.name);
        const fields = contactFields.filter((o): boolean => o.Name !== action.payload.name);
        yield put(DeleteContactFieldSuccess(fields));
        yield put(
            ShowToast({
                severity: 'info',
                message: i18n.t('customfielddeleted'),
                duration: 6000,
                onClose: HideToast,
            })
        );
    } catch (e) {
        const unableToDelete = `
        ${i18n.t('customfieldinsegment')}
        ${e?.response?.data?.Error}
        ${i18n.t('pleaseupdatesegments')}
        `;
        const errorParams = e?.response?.data?.ErrorParams;

        const matchError = matchErrorMessagesForUsageInFeatures(errorParams);

        if (matchError?.length) {
            yield put(
                ShowDialog({
                    title: i18n.t('youcannotdeletethiscustomfield'),
                    cancelLabel: i18n.t('close'),
                    body: <DeletionNotAllowedDialog type="custom_field" list={matchError} />,
                })
            );
            return;
        }

        yield put(DeleteContactFieldError());
        yield put(
            ShowDialog({
                title: i18n.t('cannotremovecustomfield'),
                body: unableToDelete,
                cancelLabel: i18n.t('close'),
            })
        );
    }
}

function* watchDeleteContactField(): any {
    yield takeLatest('@@contactfields/DELETE_START', delContactField);
}

function* setCustomFields(action): any {
    try {
        yield delay(1000);
        yield put(SetFields(action.payload.allFields, action.payload.recognizedFields));
    } catch (e) {
        Logger.error(e);
    }
}
//create watcher
function* watchGetCustomFields(): any {
    yield takeLatest('@@contactfields/GET_FIELDS', setCustomFields);
}

const customFieldsSagas = [
    fork(watchGetContactFields),
    fork(watchPutContactField),
    fork(watchDeleteContactField),
    fork(watchGetCustomFields),
];

export default customFieldsSagas;
